/**
 * Состояние кошельков четырёх сетей для веб-версии и PWA.
 *
 * Хук с состоянием (useMultiWalletState) живёт один раз — в MultiWalletProvider,
 * который монтируется в index.tsx только вне Telegram. Компоненты берут состояние
 * через useMultiWallet(): шапка, экран баланса, пополнение и вывод видят одну и
 * ту же выбранную сеть и одни и те же подключения. В мини-аппе провайдера нет,
 * и useMultiWallet() отдаёт инертное значение — весь этот слой там не задействован.
 */

import { useState, useEffect, useCallback, useMemo, useRef, createContext, useContext } from 'react';
import { useTonWallet, useTonConnectUI, useTonAddress } from '@tonconnect/ui-react';
import { useAppKit, useAppKitAccount, useAppKitProvider } from '@reown/appkit/react';
import { useAppKitConnection } from '@reown/appkit-adapter-solana/react';
import axios from 'axios';
import { Connection } from '@solana/web3.js';
import { isTronAvailable, connectTron, payTron } from '../helpers/tronBrowser';
import { payEvm, Eip1193Provider } from '../helpers/evmBrowser';
import { paySolana, SolanaWalletProvider } from '../helpers/solanaBrowser';
import {
  WalletFamily,
  connectInjected,
  hasInjectedProvider,
  injectedEvmProvider,
  injectedSolanaProvider,
} from '../helpers/injectedWallets';
import {
  clearPhantomSession,
  hasPhantomResponse,
  readPhantomConnectResponse,
  storedPhantomSession,
  stripPhantomResponseFromUrl,
} from '../helpers/phantomDeepLink';
import {
  LinkHandoff,
  WalletLinkError,
  defaultNetworkOfFamily,
  readLinkHandoff,
  stripLinkHandoffFromUrl,
  submitLinkedAddress,
} from '../helpers/walletLink';
import {
  disconnectAllReownWallets,
  isAppKitReady,
  openAppKitConnect,
  prepareWalletReturn,
} from '../providers/ReownProvider';
import {
  NETWORKS,
  NETWORK_ORDER,
  SupportedNetwork,
  SupportedCurrency,
  NetworkMeta,
  truncateAddress,
  isEvmNetwork,
  EvmNetwork,
} from '../constants/networks';

export type { SupportedNetwork, SupportedCurrency, NetworkMeta } from '../constants/networks';
export { NETWORKS, NETWORK_ORDER } from '../constants/networks';

export interface WalletBalances {
  [currency: string]: number;
}

export interface PayFromWalletParams {
  network: SupportedNetwork;
  to: string;
  amount: number;
  currency: SupportedCurrency;
}

export interface PayFromWalletResult {
  txHash: string;
  from: string;
}

export interface MultiWalletState {
  /** Есть ли провайдер (вне Telegram). В мини-аппе — false. */
  enabled: boolean;
  activeNetwork: SupportedNetwork;
  setActiveNetwork: (net: SupportedNetwork) => void;
  currentMeta: NetworkMeta;
  isConnected: boolean;
  currentAddress: string | null;
  truncatedAddress: string;
  /** Адреса по всем сетям (null — не подключено). */
  addresses: Record<SupportedNetwork, string | null>;
  /**
   * Адреса, привязанные к аккаунту на сервере (user_linked_wallets).
   * Живут дольше соединения: человек привязал кошелёк в браузере кошелька,
   * вернулся в PWA — соединения там нет, а привязка есть, и депозит с этого
   * адреса сервер по-прежнему зачислит на его аккаунт.
   */
  linkedAddresses: Record<SupportedNetwork, string | null>;
  /** Что показывать в интерфейсе: живое соединение, иначе привязка на сервере. */
  displayAddresses: Record<SupportedNetwork, string | null>;
  /** Перечитать привязки с сервера. */
  refreshLinked: () => Promise<void>;
  connectedNetworks: Record<SupportedNetwork, boolean>;
  connect: (network?: SupportedNetwork) => Promise<void>;
  /**
   * Подключение именно через WalletConnect (окно Reown AppKit), в обход выбора
   * встроенного браузера кошелька. С телефона обычный connect() ведёт в браузер
   * кошелька, потому что этот путь не зависит от relay и от разрешённых доменов
   * проекта WalletConnect; но отнимать рабочий для кого-то путь нельзя.
   */
  connectViaWalletConnect: (network: SupportedNetwork) => Promise<void>;
  disconnect: (network?: SupportedNetwork) => Promise<void>;
  connecting: boolean;
  balancesLoading: boolean;
  walletBalances: WalletBalances;
  refreshBalances: () => Promise<void>;
  /** Оплата с подключённого кошелька сети. Для TON используется TonConnect отдельно. */
  payFromWallet: (params: PayFromWalletParams) => Promise<PayFromWalletResult>;
  canPayFromWallet: (network: SupportedNetwork) => boolean;
  /** Почему кошелёк сети сейчас недоступен (для подсказки в интерфейсе). */
  unavailableReason: (network: SupportedNetwork) => string | null;
  /**
   * Окно «открыть во встроенном браузере кошелька». Для TRON и Solana с
   * телефона это единственный путь подключения, для EVM — быстрый в обход
   * WalletConnect.
   */
  walletPickerFamily: WalletFamily | null;
  openWalletPicker: (family: WalletFamily) => void;
  closeWalletPicker: () => void;
  /** Ход привязки внутри браузера кошелька (страница открыта по ссылке с токеном). */
  linkHandoff: LinkHandoffState;
  /** Почему последняя привязка не удалась. Раньше это молча уходило в консоль. */
  linkNotice: string | null;
  dismissLinkNotice: () => void;
}

export type LinkHandoffStatus = 'idle' | 'connecting' | 'linking' | 'done' | 'error';

/**
 * Состояние страницы, открытой внутри браузера кошелька по ссылке с
 * приглашением на привязку. Отдельно от обычного подключения: здесь нет ни
 * выбора сети, ни AppKit — только «дождались провайдера, спросили адрес,
 * привязали к аккаунту, показали результат».
 */
export interface LinkHandoffState {
  /** В адресе страницы был токен привязки. */
  active: boolean;
  status: LinkHandoffStatus;
  family: WalletFamily | null;
  chain: SupportedNetwork | null;
  address: string | null;
  walletLabel: string | null;
  errorCode: string | null;
  /** Код ответа сервера и его сообщение — для отчёта, а не для человека. */
  errorDetail: string | null;
  retry: () => void;
  dismiss: () => void;
}

const IDLE_HANDOFF: LinkHandoffState = {
  active: false,
  status: 'idle',
  family: null,
  chain: null,
  address: null,
  walletLabel: null,
  errorCode: null,
  errorDetail: null,
  retry: () => undefined,
  dismiss: () => undefined,
};

const EMPTY_ADDRESSES: Record<SupportedNetwork, string | null> = { ton: null, polygon: null, bsc: null, ethereum: null, solana: null, tron: null };
const EMPTY_CONNECTED: Record<SupportedNetwork, boolean> = { ton: false, polygon: false, bsc: false, ethereum: false, solana: false, tron: false };

export const INERT_MULTI_WALLET: MultiWalletState = {
  enabled: false,
  activeNetwork: 'ton',
  setActiveNetwork: () => undefined,
  currentMeta: NETWORKS.ton,
  isConnected: false,
  currentAddress: null,
  truncatedAddress: '',
  addresses: EMPTY_ADDRESSES,
  linkedAddresses: EMPTY_ADDRESSES,
  displayAddresses: EMPTY_ADDRESSES,
  refreshLinked: async () => undefined,
  connectedNetworks: EMPTY_CONNECTED,
  connect: async () => undefined,
  connectViaWalletConnect: async () => undefined,
  disconnect: async () => undefined,
  connecting: false,
  balancesLoading: false,
  walletBalances: {},
  refreshBalances: async () => undefined,
  payFromWallet: async () => {
    throw new Error('Multi-network wallets are not available here');
  },
  canPayFromWallet: () => false,
  unavailableReason: () => 'unavailable',
  walletPickerFamily: null,
  openWalletPicker: () => undefined,
  closeWalletPicker: () => undefined,
  linkHandoff: IDLE_HANDOFF,
  linkNotice: null,
  dismissLinkNotice: () => undefined,
};

function isMobileUa(): boolean {
  return typeof navigator !== 'undefined' && /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent);
}

/** Реальное состояние. Вызывается ровно один раз — из MultiWalletProvider. */
export function useMultiWalletState(): MultiWalletState {
  const [activeNetwork, setActiveNetworkState] = useState<SupportedNetwork>(() => {
    try {
      const saved = localStorage.getItem('wt_active_network') as SupportedNetwork | null;
      if (saved && NETWORKS[saved]) return saved;
    } catch {}
    return 'ton';
  });

  const setActiveNetwork = useCallback((net: SupportedNetwork) => {
    setActiveNetworkState(net);
    try {
      localStorage.setItem('wt_active_network', net);
    } catch {}
  }, []);

  // 1. TON
  const tonWallet = useTonWallet();
  const [tonConnectUI] = useTonConnectUI();
  const tonAddress = useTonAddress();

  // 2. Reown AppKit: EVM (Polygon) и Solana.
  // useAppKit() бросает исключение, если createAppKit не отработал (см. ReownProvider).
  // Раньше это глушилось молча и кнопка «Подключить» ничего не делала — теперь
  // состояние явно помечается как недоступное и человеку показывается причина.
  const appKitReady = isAppKitReady();
  let appKitOpen: ((opts?: any) => Promise<unknown>) | null = null;
  try {
    const kit = useAppKit();
    if (kit?.open) appKitOpen = kit.open as (opts?: any) => Promise<unknown>;
  } catch {
    appKitOpen = null;
  }
  const evmAccount = useAppKitAccount({ namespace: 'eip155' });
  const solAccount = useAppKitAccount({ namespace: 'solana' });
  const { walletProvider: evmProvider } = useAppKitProvider<Eip1193Provider>('eip155');
  const { walletProvider: solProvider } = useAppKitProvider<SolanaWalletProvider>('solana');
  const { connection: solConnection } = useAppKitConnection();

  // 3. TRON (TronLink / кошельки с injected tronWeb)
  const [tronAddress, setTronAddress] = useState<string | null>(() => {
    try {
      return localStorage.getItem('wt_tron_address') || null;
    } catch {
      return null;
    }
  });
  const [connecting, setConnecting] = useState(false);
  const [walletPickerFamily, setWalletPickerFamily] = useState<WalletFamily | null>(null);

  // Подключение к провайдеру, который кошелёк подставил прямо в страницу
  // (встроенный браузер кошелька, расширение в десктопном браузере). AppKit о
  // нём не знает, а состояние приложения знать обязано: иначе подключённый
  // кошелёк числится неподключённым, и оплата из него оказывается недоступна.
  const [injectedEvmAddress, setInjectedEvmAddress] = useState<string | null>(null);
  const [injectedSolanaAddress, setInjectedSolanaAddress] = useState<string | null>(null);

  // Phantom, подключённый его родным протоколом глубоких ссылок: ни AppKit
  // (у Phantom нет WalletConnect), ни провайдера в странице (мы не в его
  // браузере) — только сессия, которую вернула сама переписка с кошельком.
  // Она переживает перезагрузку, поэтому читается из хранилища при старте.
  const [phantomAddress, setPhantomAddress] = useState<string | null>(
    () => storedPhantomSession()?.address || null
  );

  const openWalletPicker = useCallback((family: WalletFamily) => setWalletPickerFamily(family), []);
  const closeWalletPicker = useCallback(() => setWalletPickerFamily(null), []);

  // EVM-кошелёк один на все EVM-сети: тот же адрес в Polygon, BNB Chain и Ethereum.
  const evmAddress = (evmAccount.isConnected && evmAccount.address ? evmAccount.address : null) || injectedEvmAddress;
  const solanaAddress =
    (solAccount.isConnected && solAccount.address ? solAccount.address : null) ||
    injectedSolanaAddress ||
    phantomAddress;
  const addresses = useMemo<Record<SupportedNetwork, string | null>>(
    () => ({
      ton: tonWallet && tonAddress ? tonAddress : null,
      polygon: evmAddress,
      bsc: evmAddress,
      ethereum: evmAddress,
      solana: solanaAddress,
      tron: tronAddress,
    }),
    [tonWallet, tonAddress, evmAddress, solanaAddress, tronAddress]
  );

  const connectedNetworks = useMemo<Record<SupportedNetwork, boolean>>(
    () => ({
      ton: Boolean(addresses.ton),
      polygon: Boolean(addresses.polygon),
      bsc: Boolean(addresses.bsc),
      ethereum: Boolean(addresses.ethereum),
      solana: Boolean(addresses.solana),
      tron: Boolean(addresses.tron),
    }),
    [addresses]
  );

  const currentAddress = addresses[activeNetwork];
  const isConnected = Boolean(currentAddress);

  // Приглашение на привязку из адресной строки: страница открыта внутри
  // браузера кошелька, JWT приложения там нет. Читаем один раз при монтировании
  // и сразу убираем токен из адреса — он живёт 15 минут, и в истории браузера
  // кошелька ему делать нечего.
  const handoffRef = useRef<LinkHandoff | null>(null);
  const handoffReadRef = useRef(false);
  if (!handoffReadRef.current && typeof window !== 'undefined') {
    handoffReadRef.current = true;
    handoffRef.current = readLinkHandoff();
  }
  const handoffToken = handoffRef.current?.token ?? null;

  const [linkNotice, setLinkNotice] = useState<string | null>(null);
  const dismissLinkNotice = useCallback(() => setLinkNotice(null), []);

  // Привязка адресов к аккаунту — по ним сервер атрибутирует депозиты.
  const linkedRef = useRef<Record<string, string>>({});
  const linkWalletToServer = useCallback(
    async (chain: SupportedNetwork, address: string) => {
      if (!address || linkedRef.current[chain] === address) return;
      linkedRef.current[chain] = address;
      try {
        await submitLinkedAddress({
          chain,
          address,
          walletName: NETWORKS[chain].name + ' Wallet',
          token: handoffToken,
        });
      } catch (e) {
        delete linkedRef.current[chain];
        // Молчать здесь нельзя: именно так «подключил, а в приложении пусто»
        // и выглядело изнутри — единственным следом был console.warn.
        setLinkNotice(e instanceof WalletLinkError ? e.code : 'link_failed');
      }
    },
    [handoffToken]
  );

  useEffect(() => {
    NETWORK_ORDER.forEach((net) => {
      const addr = addresses[net];
      if (addr) linkWalletToServer(net, addr);
    });
  }, [addresses, linkWalletToServer]);

  // Привязки с сервера. Соединение с кошельком живёт в этой вкладке, привязка —
  // в аккаунте: человек подключил MetaMask в браузере кошелька, вернулся в PWA,
  // и там соединения нет. Без этого списка PWA показывало «не подключено» уже
  // привязанному кошельку и прятало пополнение по хешу.
  const [linkedAddresses, setLinkedAddresses] = useState<Record<SupportedNetwork, string | null>>(EMPTY_ADDRESSES);

  const refreshLinked = useCallback(async () => {
    try {
      const res = await axios.get('/api/wallet/link.php');
      const list = res.data?.wallets || {};
      const next: Record<SupportedNetwork, string | null> = { ...EMPTY_ADDRESSES };
      NETWORK_ORDER.forEach((net) => {
        const item = list[net];
        // is_active = 0 означает «отвязан»: строка остаётся ради атрибуции
        // старых депозитов, но показывать её как привязку нельзя.
        if (item && item.address && item.is_active !== false) next[net] = item.address;
      });
      setLinkedAddresses(next);
    } catch {
      // Не авторизован или сеть недоступна — привязки просто неизвестны.
    }
  }, []);

  useEffect(() => {
    refreshLinked();
  }, [refreshLinked]);

  // Подключились в этой вкладке — привязка заведомо есть, ждать ответа сервера незачем.
  useEffect(() => {
    setLinkedAddresses((prev) => {
      let changed = false;
      const next = { ...prev };
      NETWORK_ORDER.forEach((net) => {
        const addr = addresses[net];
        if (addr && next[net] !== addr) {
          next[net] = addr;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [addresses]);

  const displayAddresses = useMemo<Record<SupportedNetwork, string | null>>(() => {
    const out: Record<SupportedNetwork, string | null> = { ...EMPTY_ADDRESSES };
    NETWORK_ORDER.forEach((net) => {
      out[net] = addresses[net] || linkedAddresses[net] || null;
    });
    return out;
  }, [addresses, linkedAddresses]);

  // Балансы подключённого кошелька в активной сети (кроме TON — у него свой слой в redux).
  const [walletBalances, setWalletBalances] = useState<Record<SupportedNetwork, WalletBalances>>({
    ton: {},
    polygon: {},
    bsc: {},
    ethereum: {},
    solana: {},
    tron: {},
  });
  const [balancesLoading, setBalancesLoading] = useState(false);

  const refreshBalances = useCallback(async () => {
    // Баланс кошелька читается по адресу, а не по соединению: привязанный
    // адрес показывается с балансом так же, как подключённый.
    const address = displayAddresses[activeNetwork];
    if (!address || activeNetwork === 'ton') return;
    setBalancesLoading(true);
    try {
      // Внутри браузера кошелька JWT нет — там баланс читается по токену
      // привязки, иначе подтверждение «привязано» остаётся словом без числа.
      const tokenQuery = handoffToken ? `&linkToken=${encodeURIComponent(handoffToken)}` : '';
      const res = await axios.get(
        `/api/wallet/balances-proxy.php?network=${activeNetwork}&address=${encodeURIComponent(address)}${tokenQuery}`
      );
      const b = res.data?.balances || {};
      const mapped: WalletBalances = {};
      Object.keys(b).forEach((k) => {
        mapped[k.toUpperCase()] = Number(b[k]) || 0;
      });
      setWalletBalances((prev) => ({ ...prev, [activeNetwork]: mapped }));
    } catch (err) {
      console.warn('[useMultiWallet] Failed to fetch balances:', err);
    } finally {
      setBalancesLoading(false);
    }
  }, [activeNetwork, displayAddresses, handoffToken]);

  useEffect(() => {
    refreshBalances();
  }, [refreshBalances]);

  const unavailableReason = useCallback(
    (network: SupportedNetwork): string | null => {
      if (isEvmNetwork(network) || network === 'solana') {
        const family: WalletFamily = network === 'solana' ? 'solana' : 'evm';
        // Внутри браузера кошелька провайдер уже в странице, и AppKit не нужен.
        // На телефоне снаружи путь через браузер кошелька есть всегда, поэтому
        // «коннектор недоступен» там больше не тупик.
        if (hasInjectedProvider(family) || isMobileUa()) return null;
        return appKitReady && appKitOpen ? null : 'connector_unavailable';
      }
      if (network === 'tron') {
        return isTronAvailable() ? null : isMobileUa() ? 'tron_open_in_app' : 'tron_install_extension';
      }
      return null;
    },
    [appKitReady, appKitOpen]
  );

  const connectViaWalletConnect = useCallback(
    async (net: SupportedNetwork) => {
      if (!appKitOpen) {
        throw new Error('connector_unavailable');
      }
      // Метаданные с redirect должны попасть в предложение сессии до того,
      // как оно уйдёт кошельку, — и заодно так поднимается соединение с
      // релеем WalletConnect. Но ЖДАТЬ этого нельзя: раньше здесь стояло
      // ожидание до 2,5 секунды, и всё это время нажатие «Подключить» не
      // давало на экране ничего — окно выбора кошелька просто не появлялось.
      // Плюс это же ожидание было щелью, в которую активная сеть AppKit
      // успевала уехать обратно на eip155, и Solana открывалась мёртвым
      // окном. Запускаем и идём дальше: пока человек читает список
      // кошельков, провайдер успевает подняться, а redirect — дописаться.
      void prepareWalletReturn();
      // Переключение сети и открытие экрана Connect — вместе: AppKit при
      // несовпадении пространств имён переключает сеть ВМЕСТО открытия
      // экрана. Подробности в openAppKitConnect.
      await openAppKitConnect(net, appKitOpen);
    },
    [appKitOpen]
  );

  const connect = useCallback(
    async (network?: SupportedNetwork) => {
      const net = network ?? activeNetwork;
      if (network && network !== activeNetwork) setActiveNetwork(network);

      if (net === 'ton') {
        await tonConnectUI.openModal();
        return;
      }
      if (isEvmNetwork(net) || net === 'solana') {
        const family: WalletFamily = net === 'solana' ? 'solana' : 'evm';

        // Страница уже внутри браузера кошелька: провайдер в странице,
        // подключаемся напрямую. Через AppKit здесь пришлось бы выбирать из
        // списка тот кошелёк, в котором человек и так находится, а для Solana
        // это ещё и не работает — у Phantom нет WalletConnect.
        if (hasInjectedProvider(family)) {
          setConnecting(true);
          try {
            const injected = await connectInjected(family);
            if (family === 'solana') {
              setInjectedSolanaAddress(injected.address);
            } else {
              setInjectedEvmAddress(injected.address);
            }
            await linkWalletToServer(net, injected.address);
            setLinkedAddresses((prev) => ({ ...prev, [net]: injected.address }));
          } finally {
            setConnecting(false);
          }
          return;
        }

        // Телефон снаружи.
        //
        // Для EVM это WalletConnect: он даёт круг, которого ждут от кошелька, —
        // нажал, подтвердил в MetaMask или Trust, вернулся в приложение, где
        // человек уже авторизован. Встроенный браузер кошелька такого круга не
        // даёт: это отдельный WebView, вход туда не переносится, и после
        // привязки приходится возвращаться руками.
        //
        // Некоторое время здесь стоял безусловный уход в список встроенных
        // браузеров: считалось, что relay всё равно не доносит предложение
        // сессии. Основанием была диагностика, которая горела красным
        // независимо от состояния проекта (см. WalletDiagnostics) — то есть
        // основания не было. Список остался явным вторым путём: кнопка
        // «Открыть в приложении кошелька» на карточке кошелька и запасной
        // вариант ниже, если окно WalletConnect не поднялось.
        //
        // Для Solana WalletConnect не подходит по-настоящему: у Phantom его
        // нет, и AppKit на телефоне всё равно уводит в его встроенный браузер,
        // только молча.
        if (isMobileUa() && family === 'solana') {
          setWalletPickerFamily(family);
          return;
        }

        try {
          await connectViaWalletConnect(net);
        } catch (err) {
          if (!isMobileUa()) throw err;
          // Окно WalletConnect не поднялось — на телефоне это не тупик:
          // остаётся путь через встроенный браузер кошелька.
          setWalletPickerFamily(family);
        }
        return;
      }
      // TRON. Своего адаптера у Reown нет, поэтому подключение возможно только
      // там, где кошелёк сам подставил tronWeb в страницу: расширение в
      // десктопном браузере или встроенный браузер приложения на телефоне.
      // Раньше телефон уводило прямо в TronLink — теперь спрашиваем, в каком
      // кошельке открыть: TRON есть и у Trust, Bitget, OKX.
      if (!isTronAvailable()) {
        if (isMobileUa()) {
          setWalletPickerFamily('tron');
          return;
        }
        window.open('https://www.tronlink.org/', '_blank', 'noopener,noreferrer');
        throw new Error('tron_install_extension');
      }
      setConnecting(true);
      try {
        const wallet = await connectTron();
        setTronAddress(wallet.address);
        try {
          localStorage.setItem('wt_tron_address', wallet.address);
        } catch {}
      } finally {
        setConnecting(false);
      }
    },
    [activeNetwork, setActiveNetwork, tonConnectUI, linkWalletToServer, connectViaWalletConnect]
  );

  // Страница открылась внутри браузера кошелька по ссылке «Открыть в
  // приложении кошелька». Человек уже выбрал кошелёк в приложении — второй
  // раз спрашивать не за чем, подключаемся и привязываем сами.
  //
  // Раньше признак «шли подключаться» ставился в sessionStorage перед уходом в
  // кошелёк, а читался уже внутри кошелька. Это разные хранилища: отметка не
  // доезжала никогда, автоподключение не срабатывало ни разу, и внутри кошелька
  // приходилось жать «Подключить» второй раз. Теперь признак и токен привязки
  // едут в адресной строке — единственным каналом, который переживает переход
  // между приложениями.
  const [linkHandoffState, setLinkHandoffState] = useState<LinkHandoffState>(IDLE_HANDOFF);

  const runLinkHandoff = useCallback(
    async (handoff: LinkHandoff) => {
      const chain: SupportedNetwork =
        handoff.chain && NETWORKS[handoff.chain] ? handoff.chain : defaultNetworkOfFamily(handoff.family);

      setLinkHandoffState((prev) => ({
        ...prev,
        active: true,
        status: 'connecting',
        family: handoff.family,
        chain,
        errorCode: null,
        errorDetail: null,
      }));
      setConnecting(true);

      try {
        const injected = await connectInjected(handoff.family);
        setLinkHandoffState((prev) => ({
          ...prev,
          status: 'linking',
          address: injected.address,
          walletLabel: injected.walletLabel,
        }));

        await submitLinkedAddress({
          chain,
          address: injected.address,
          walletName: injected.walletLabel,
          token: handoff.token,
        });
        // Отмечаем как привязанный здесь же: иначе эффект ниже увидит новый
        // адрес и пошлёт второй, уже лишний, запрос на привязку.
        linkedRef.current[chain] = injected.address;

        if (handoff.family === 'tron') {
          setTronAddress(injected.address);
          try {
            localStorage.setItem('wt_tron_address', injected.address);
          } catch {}
        } else if (handoff.family === 'solana') {
          setInjectedSolanaAddress(injected.address);
        } else {
          setInjectedEvmAddress(injected.address);
        }
        setLinkedAddresses((prev) => ({ ...prev, [chain]: injected.address }));
        setActiveNetwork(chain);
        setLinkHandoffState((prev) => ({ ...prev, status: 'done' }));
      } catch (err: any) {
        const code = err instanceof WalletLinkError ? err.code : err?.message || 'link_failed';
        const detail =
          err instanceof WalletLinkError
            ? [err.code, err.status ? `HTTP ${err.status}` : null, err.message !== err.code ? err.message : null]
                .filter(Boolean)
                .join(' · ')
            : String(err?.message || err);
        setLinkHandoffState((prev) => ({
          ...prev,
          status: 'error',
          errorCode: String(code),
          errorDetail: detail,
        }));
      } finally {
        setConnecting(false);
      }
    },
    [setActiveNetwork]
  );

  useEffect(() => {
    const handoff = handoffRef.current;
    if (!handoff) return;
    stripLinkHandoffFromUrl();
    runLinkHandoff(handoff);
  }, [runLinkHandoff]);

  // Возврат из Phantom по его родному протоколу. В отличие от привязки из
  // встроенного браузера кошелька здесь мы в своём приложении: человек
  // авторизован, JWT на месте, одноразовый токен не нужен — привязка идёт
  // обычным запросом.
  const phantomHandledRef = useRef(false);
  useEffect(() => {
    if (phantomHandledRef.current || !hasPhantomResponse()) return;
    phantomHandledRef.current = true;

    const outcome = readPhantomConnectResponse();
    // Чистим адрес в любом случае: метка возврата бесполезна, а ответ Phantom
    // незачем оставлять в истории и в referrer.
    stripPhantomResponseFromUrl();
    if (!outcome) return;

    if (outcome.status === 'connected') {
      setPhantomAddress(outcome.session.address);
      setActiveNetwork('solana');
      setLinkedAddresses((prev) => ({ ...prev, solana: outcome.session.address }));
      linkWalletToServer('solana', outcome.session.address);
      return;
    }
    if (outcome.status === 'error') {
      // Отказ человека (rejected) молчит: он знает, что нажал «Отклонить».
      // Всё остальное — поломка, и о ней нужно сказать, иначе возврат из
      // кошелька выглядит как «ничего не произошло».
      setLinkNotice('phantom_connect_failed');
      console.warn('[useMultiWallet] Phantom connect failed:', outcome.detail);
    }
  }, [linkWalletToServer, setActiveNetwork]);

  const linkHandoff = useMemo<LinkHandoffState>(
    () => ({
      ...linkHandoffState,
      retry: () => {
        const handoff = handoffRef.current;
        if (handoff) runLinkHandoff(handoff);
      },
      dismiss: () => setLinkHandoffState((prev) => ({ ...prev, active: false })),
    }),
    [linkHandoffState, runLinkHandoff]
  );

  const disconnect = useCallback(
    async (network?: SupportedNetwork) => {
      const net = network ?? activeNetwork;

      // Разрыв соединения с кошельком — не то же самое, что отвязка адреса от
      // аккаунта. Соединения может не быть вовсе: адрес привязан на сервере, а
      // кошелёк подключали в другом браузере. TonConnect в этом случае бросает
      // WalletNotConnectedError (`disconnect()` начинается с
      // `if (!this.connected) throw`), и раньше исключение уносило с собой
      // остаток функции — отвязка на сервере не выполнялась, и кнопка
      // «Отключить» для TON не делала ничего, сколько ни нажимай.
      try {
        if (net === 'ton') {
          await tonConnectUI.disconnect();
        } else if (isEvmNetwork(net) || net === 'solana') {
          if (net === 'solana') {
            setInjectedSolanaAddress(null);
            setPhantomAddress(null);
            clearPhantomSession();
          } else {
            setInjectedEvmAddress(null);
          }
          await disconnectAllReownWallets(net === 'solana' ? 'solana' : 'eip155');
        } else if (net === 'tron') {
          setTronAddress(null);
          try {
            localStorage.removeItem('wt_tron_address');
          } catch {}
        }
      } catch (err) {
        console.warn('[useMultiWallet] disconnect provider error:', err);
      }

      delete linkedRef.current[net];
      setLinkedAddresses((prev) => (prev[net] ? { ...prev, [net]: null } : prev));
      try {
        await axios.delete(`/api/wallet/link.php?chain=${net}`);
      } catch {}
    },
    [activeNetwork, tonConnectUI]
  );

  // Внутри браузера кошелька соединения AppKit нет — провайдер кошелёк
  // подставил прямо в страницу. Платить оттуда можно им же, иначе человек,
  // подключившийся в кошельке, увидел бы «оплата недоступна» ровно там, где
  // кошелёк под рукой.
  const evmPayProvider = useMemo<Eip1193Provider | null>(
    () => evmProvider || (injectedEvmProvider() as Eip1193Provider | null),
    [evmProvider]
  );

  const solanaPayProvider = useMemo<SolanaWalletProvider | null>(() => {
    if (solProvider) return solProvider;
    const injected = injectedSolanaProvider() as any;
    if (!injected || typeof injected.signAndSendTransaction !== 'function') return null;
    return {
      async sendTransaction(transaction: any) {
        const res = await injected.signAndSendTransaction(transaction);
        return res?.signature || res;
      },
      signAndSendTransaction: injected.signAndSendTransaction.bind(injected),
    };
  }, [solProvider]);

  // Тот же узел, что читает балансы на сервере (balances-proxy.php).
  const solanaPayConnection = useMemo(
    () => solConnection || (solanaPayProvider ? new Connection('https://api.mainnet-beta.solana.com', 'confirmed') : null),
    [solConnection, solanaPayProvider]
  );

  const canPayFromWallet = useCallback(
    (network: SupportedNetwork) => {
      if (!addresses[network]) return false;
      if (isEvmNetwork(network)) return Boolean(evmPayProvider);
      if (network === 'solana') return Boolean(solanaPayProvider && solanaPayConnection);
      if (network === 'tron') return isTronAvailable();
      return false;
    },
    [addresses, evmPayProvider, solanaPayProvider, solanaPayConnection]
  );

  const payFromWallet = useCallback(
    async ({ network, to, amount, currency }: PayFromWalletParams): Promise<PayFromWalletResult> => {
      const from = addresses[network];
      if (!from) throw new Error('Wallet is not connected');

      if (isEvmNetwork(network)) {
        if (!evmPayProvider) throw new Error('connector_unavailable');
        const txHash = await payEvm(evmPayProvider, { network: network as EvmNetwork, from, to, amount, currency });
        return { txHash, from };
      }
      if (network === 'solana') {
        if (!solanaPayProvider || !solanaPayConnection) throw new Error('connector_unavailable');
        if (currency !== 'SOL' && currency !== 'USDC') throw new Error('Unsupported currency');
        const txHash = await paySolana(solanaPayProvider, solanaPayConnection as any, { from, to, amount, currency });
        return { txHash, from };
      }
      if (network === 'tron') {
        if (currency !== 'TRX' && currency !== 'USDT') throw new Error('Unsupported currency');
        const res = await payTron({ to, amount, currency });
        return { txHash: res.txHash, from: res.fromAddress };
      }
      throw new Error('Use TonConnect for TON payments');
    },
    [addresses, evmPayProvider, solanaPayProvider, solanaPayConnection]
  );

  return {
    enabled: true,
    activeNetwork,
    setActiveNetwork,
    currentMeta: NETWORKS[activeNetwork],
    isConnected,
    currentAddress,
    truncatedAddress: truncateAddress(currentAddress || ''),
    addresses,
    linkedAddresses,
    displayAddresses,
    refreshLinked,
    connectedNetworks,
    connect,
    connectViaWalletConnect,
    disconnect,
    connecting,
    balancesLoading,
    walletBalances: walletBalances[activeNetwork],
    refreshBalances,
    payFromWallet,
    canPayFromWallet,
    unavailableReason,
    walletPickerFamily,
    openWalletPicker,
    closeWalletPicker,
    linkHandoff,
    linkNotice,
    dismissLinkNotice,
  };
}

/** Контекст живёт здесь, а не в провайдере, чтобы не было цикла импортов. */
export const MultiWalletContext = createContext<MultiWalletState>(INERT_MULTI_WALLET);

export function useMultiWallet(): MultiWalletState {
  return useContext(MultiWalletContext);
}
